import { TranslocoModule, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Component, DestroyRef, inject, signal, ViewChild } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { startWith, map, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { CdkScrollable, ScrollingModule } from '@angular/cdk/scrolling';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { TableView, TypeOfColumn, TableColumnInfo, SortColumn } from 'design-system';
import { Router } from '@angular/router';
import {
  CatalogProduct,
  FeaturedProduct,
  GroupedProduct,
} from '../../interfaces/product.model';
import { AvailabilityChip } from '../components/availability-chip/availability-chip';
import { CategoryChip } from '../components/category-chip/category-chip';
import { AddToCartButton } from '../components/add-to-cart-button/add-to-cart-button';
import { AddToCartDialog, AddToCartDialogResult } from '../components/add-to-cart-dialog/add-to-cart-dialog';
import { Q8Breakpoints, DynamicRouteService } from 'core';
import { ProductsService, PlcLookupItem } from '../../services/products.service';
import { CartService } from '../../services/cart.service';
import { PRODUCTS_DESCRIBE } from '../constants';

@Component({
  providers: [provideTranslocoScope('products')],
  selector: 'prd-products-list',
  imports: [
    AsyncPipe,
    TranslocoModule,
    ReactiveFormsModule,
    ScrollingModule,
    MatDividerModule,
    MatCardModule,
    MatButtonModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatChipsModule,
    MatCheckboxModule,
    MatExpansionModule,
    TableView,
    TranslocoCurrencyPipe,
    AvailabilityChip,
    CategoryChip,
    AddToCartButton,
    MatPaginatorModule,
  ],
  templateUrl: './products-list.html',
  styleUrl: './products-list.scss',
})
export class ProductsList {
  private transloco = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private breakpointObserver = inject(BreakpointObserver);
  private productsService = inject(ProductsService);
  protected cartService = inject(CartService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private dynamicRouteService = inject(DynamicRouteService);

  filtersForm = this.fb.group({
    searchText: [''],
    plc1: [[] as string[]],
    plc2: [[] as string[]],
    tipiOlio: [[] as string[]],
    segmentiMercato: [[] as string[]],
    disponibili: [false],
    prezzoMin: [null as number | null],
    prezzoMax: [null as number | null],
    industria: [[] as string[]],
    applicazione: [[] as string[]],
    tipoOlio: [[] as string[]],
    viscosita: [[] as string[]],
    specifiche: [[] as string[]],
    competitorMarchio: [''],
    competitorNomeProdotto: [''],
  });
  filtersOpen = signal(false);
  advancedFiltersOpen = signal(false);

  protected readonly technicalOptionKeys: Record<string, string> = {
    "Automotive": "products.ui.automotive",
    "General Industry": "products.ui.generalIndustry",
    "Marine": "products.ui.marine",
    "Agricoltura": "products.ui.agricoltura",
    "Energia": "products.ui.energia",
    "Motore": "products.ui.motore",
    "Trasmissione": "products.ui.trasmissione",
    "Idraulico": "products.ui.idraulico",
    "Compressori": "products.ui.compressori",
    "Ingranaggi": "products.ui.ingranaggi",
    "Minerale": "products.ui.minerale",
    "Semisintetico": "products.ui.semisintetico",
    "Sintetico": "products.ui.sintetico",
    "Vegetale": "products.ui.vegetale"
};

  technicalFilterCategories: { key: string; label: string; options: string[] }[] = [
    { key: 'industria', get label() { return translate<string>('products.ui.industria'); }, options: ['Automotive', 'General Industry', 'Marine', 'Agricoltura', 'Energia'] },
    { key: 'applicazione', get label() { return translate<string>('products.ui.applicazione'); }, options: ['Motore', 'Trasmissione', 'Idraulico', 'Compressori', 'Ingranaggi'] },
    { key: 'tipoOlio', get label() { return translate<string>('products.ui.tipoDiOlio'); }, options: ['Minerale', 'Semisintetico', 'Sintetico', 'Vegetale'] },
    { key: 'viscosita', get label() { return translate<string>('products.ui.viscosita'); }, options: ['0W-20', '5W-30', '5W-40', '10W-40', '15W-40', '20W-50'] },
    { key: 'specifiche', get label() { return translate<string>('products.ui.specifiche'); }, options: ['ACEA A3/B4', 'ACEA C3', 'API SN', 'MB 229.5', 'VW 502.00'] },
  ];
  selectedView = 'list';
  isMobile = false;

  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  currentSortColumns: SortColumn[] = [];

  private readonly sortPropertyMap: Record<string, string> = {
    nomeProdotto: 'ProdottoMaster.NomeProdotto',
    sku: 'Sku',
    imballoDescrizione: 'NomeImballo',
    scala: 'Scala',
    prezzo: 'Prezzo',
    plc1: 'Plc1',
    plc2: 'Plc2',
    stock: 'Stock.Availability',
  };

  plc1Options: PlcLookupItem[] = [];
  plc2Options: PlcLookupItem[] = [];
  tipiOlioOptions: PlcLookupItem[] = [];
  segmentiMercatoOptions: PlcLookupItem[] = [];
  plcType = signal('');

  get visiblePlc1Options(): PlcLookupItem[] {
    return this.filterPlcOptionsByType(this.plc1Options);
  }

  get visiblePlc2Options(): PlcLookupItem[] {
    return this.filterPlcOptionsByType(this.plc2Options);
  }

  onPlcTypeChange(type: string) {
    this.plcType.set(type);
    this.prunePlcSelection('plc1', this.plc1Options);
    this.prunePlcSelection('plc2', this.plc2Options);
    if (type === 'NONOIL') {
      this.filtersForm.patchValue({ tipiOlio: [], segmentiMercato: [] });
    }
  }

  private filterPlcOptionsByType(options: PlcLookupItem[]): PlcLookupItem[] {
    const type = this.plcType();
    return type ? options.filter(o => o.type === type) : options;
  }

  private prunePlcSelection(controlName: 'plc1' | 'plc2', options: PlcLookupItem[]) {
    const control = this.filtersForm.controls[controlName];
    const allowed = new Set(this.filterPlcOptionsByType(options).map(o => o.value));
    const selected = control.value ?? [];
    const kept = selected.filter(v => allowed.has(v));
    if (kept.length !== selected.length) {
      control.setValue(kept);
    }
  }
  filteredCompetitorMarchio!: Observable<string[]>;
  filteredCompetitorNomeProdotto!: Observable<string[]>;

  // Marchi dei competitor con un corrispettivo nel catalogo (api/prodotticatalogo/filtri)
  competitorMarchioOptions: string[] = [];

  viewOptions = [
    {
      value: 'list',
      get label() { return translate<string>('products.ui.tabella'); },
      icon: 'fa-list',
      iconVariant: 'regular',
    },
    {
      value: 'card',
      get label() { return translate<string>('products.ui.card'); },
      icon: 'fa-grid-2',
      iconVariant: 'regular',
    },
    {
      value: 'grouped',
      get label() { return translate<string>('products.ui.raggruppata'); },
      icon: 'fa-layer-group',
      iconVariant: 'regular',
    },
  ];

  tableProducts: any[] = [];

  columnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'nomeProdotto',
      get label() { return translate<string>('products.ui.nomeProdotto'); },
      type: TypeOfColumn.TEXT,
      sortable: true,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'sku',
      get label() { return translate<string>('products.ui.codProdotto'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'imballoDescrizione',
      get label() { return translate<string>('products.ui.imballoQTaUom'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'badge',
      label: 'Badge',
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: CategoryChip,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'scala',
      get label() { return translate<string>('products.ui.scala'); },
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'prezzo',
      get label() { return translate<string>('products.ui.prezzo'); },
      type: TypeOfColumn.CURRENCY,
      sortable: false,
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc1',
      label: 'PLC1',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc2',
      label: 'PLC2',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'stock',
      get label() { return translate<string>('products.ui.stock'); },
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AvailabilityChip,
      labelOnOneLine: true,
      cellClass: () => 'cell-align-right',
    },
    {
      columnNameDef: 'idProdotto',
      label: '',
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AddToCartButton,
    },
  ];

  catalogProducts: CatalogProduct[] = [];

  groupedProducts: GroupedProduct[] = [];

  toggleFilters() {
    this.filtersOpen.update(v => !v);
  }

  toggleAdvancedFilters() {
    this.advancedFiltersOpen.update(v => !v);
  }

  onViewChange(value: string) {
    if (this.isMobile && value === 'list') return;

    const changesDataSource = (this.selectedView === 'grouped') !== (value === 'grouped');
    this.selectedView = value;

    if (changesDataSource) {
      this.pageIndex = 0;
      this.loadProducts();
    }
  }

  openProductDetail(productId: string) {
    let navigationArray = this.dynamicRouteService.getRoutePath(this.router, PRODUCTS_DESCRIBE, 'product-detail');
    if (navigationArray) {
      navigationArray = this.dynamicRouteService.replacePathSegments(navigationArray, [productId]);
      this.router.navigate(navigationArray, {});
    }
  }

  private _scrollable!: CdkScrollable;

  // Setter invece di ngAfterViewInit perché il carousel è dentro *transloco che potrebbe
  // ritardare il rendering e ViewChild sarebbe undefined.
  // Il setter viene chiamato da Angular nel momento esatto in cui la direttiva cdkScrollable entra nel DOM.
  @ViewChild(CdkScrollable) set scrollable(s: CdkScrollable) {
    this._scrollable = s;
    if (!s) return;
    this.updateScrollState();
    s.elementScrolled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateScrollState());
  }

  featuredProducts: FeaturedProduct[] = [];
  readonly featuredProductsLoaded = signal(false);

  private scrollInterval: ReturnType<typeof setInterval> | null = null;

  canScrollLeft = signal(false);
  canScrollRight = signal(false);

  openAddToCartDialog(event: MouseEvent, product: FeaturedProduct | CatalogProduct) {
    event.stopPropagation();

    this.dialog.open(AddToCartDialog, {
      width: '500px',
      minHeight: '300px',
      data: {
        idProdotto: product.idProdotto,
        nomeProdotto: product.nomeProdotto,
        sku: product.sku,
        nomeImballo: product.nomeImballo ?? undefined,
        unitaFatturabile: product.unitaFatturabile ?? undefined,
        prezzo: product.prezzo,
        unitaMisura: product.unitaMisura ?? undefined,
        prezzoMinimo: this.cartService.minimumPrice(product.idProdotto, product.prezzoMinimoRuolo),
      },
    }).afterClosed().subscribe((result?: AddToCartDialogResult) => {
      if (result) {
        this.cartService.updateProduct(product.idProdotto, result.quantity, result.price)
          .subscribe({
            error: error => {
              console.error('Errore durante l\'aggiunta al carrello', error);
            },
          });
      }
    });
  }

  ngOnInit() {
    this.cartService.getActiveCart()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => {
          console.error('Errore durante il caricamento del carrello', error);
        },
      });

    this.productsService.getFeaturedProducts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.featuredProducts = data.items;
          this.featuredProductsLoaded.set(true);
        },
        error: error => {
          console.error('Errore durante il caricamento dei prodotti in evidenza', error);
        },
      });

    this.loadProducts();

    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet - 1}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isMobile = result.matches;
        if (this.isMobile && this.selectedView === 'list') {
          this.selectedView = 'card';
        }
      });

    this.transloco.selectTranslation('products').subscribe(() => {
      // this.translateUI();
    });

    this.filtersForm.controls.searchText.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadProducts();
      });

    this.productsService.getPlc1Options()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.plc1Options = data.plc ?? [];
        },
        error: error => {
          console.error('Errore durante il caricamento delle opzioni PLC1', error);
        },
      });
    this.productsService.getPlc2Options()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.plc2Options = data.plc ?? [];
        },
        error: error => {
          console.error('Errore durante il caricamento delle opzioni PLC2', error);
        },
      });
    this.productsService.getProductFilters()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.tipiOlioOptions = data.tipiOlio ?? [];
          this.segmentiMercatoOptions = data.segmentiMercato ?? [];
          this.competitorMarchioOptions = (data.marchiCompetitor ?? []).map(opt => opt.label);
        },
        error: error => {
          console.error('Errore durante il caricamento dei filtri prodotto', error);
        },
      });

    this.filteredCompetitorMarchio = this.filtersForm.get('competitorMarchio')!.valueChanges.pipe(
      startWith(''),
      map(val => this._filterOptions(val ?? '', this.competitorMarchioOptions)),
    );
    // Nomi dei prodotti competitor dal backend, per il marchio scritto e il testo digitato
    this.filteredCompetitorNomeProdotto = this.filtersForm.get('competitorNomeProdotto')!.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(val => this.productsService
        .getCompetitorProducts(this.filtersForm.get('competitorMarchio')?.value ?? '', val ?? '')
        .pipe(
          map(data => (data.items ?? []).map((item: { nome: string }) => item.nome)),
          catchError(() => of([] as string[])),
        )),
    );
  }

  private _filterOptions(value: string, options: string[]): string[] {
    const lower = value.toLowerCase();
    return options.filter(o => o.toLowerCase().includes(lower));
  }

  clearFilterField(field: string) {
    this.filtersForm.get(field)?.setValue(null);
  }

  // Sistemare quando sono chiari i filtri
  resetFilters() {
    this.filtersForm.controls.searchText.setValue('', { emitEvent: false });
    this.plcType.set('');
    this.filtersForm.patchValue({
      plc1: [],
      plc2: [],
      tipiOlio: [],
      segmentiMercato: [],
      disponibili: false,
      prezzoMin: null,
      prezzoMax: null,
      industria: [],
      applicazione: [],
      tipoOlio: [],
      viscosita: [],
      specifiche: [],
      competitorMarchio: null,
      competitorNomeProdotto: null,
    });
    this.pageIndex = 0;
    this.loadProducts();
  }

  onTechnicalFilterSelect(event: { selected: boolean }, categoryKey: string, value: string) {
    const current: string[] = this.filtersForm.get(categoryKey)?.value ?? [];
    const updated = event.selected
      ? [...current, value]
      : current.filter(v => v !== value);
    this.filtersForm.get(categoryKey)?.setValue(updated);
  }

  isTechnicalFilterSelected(categoryKey: string, value: string): boolean {
    const current: string[] = this.filtersForm.get(categoryKey)?.value ?? [];
    return current.includes(value);
  }

  getActiveTechnicalFilters(): { key: string; value: string }[] {
    const active: { key: string; value: string }[] = [];
    for (const category of this.technicalFilterCategories) {
      const selected: string[] = this.filtersForm.get(category.key)?.value ?? [];
      for (const value of selected) {
        active.push({ key: category.key, value });
      }
    }
    return active;
  }

  removeTechnicalFilter(categoryKey: string, value: string) {
    const current: string[] = this.filtersForm.get(categoryKey)?.value ?? [];
    this.filtersForm.get(categoryKey)?.setValue(current.filter(v => v !== value));
  }

  applyFilters() {
    this.pageIndex = 0;
    this.loadProducts();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProducts();
  }

  onSortChange(sortColumns: SortColumn[]) {
    this.currentSortColumns = sortColumns;
    this.pageIndex = 0;
    this.loadProducts();
  }

  private buildSearchPayload(withOrderBy: boolean): any {
    const formValue = this.filtersForm.getRawValue();
    const searchObject: any = {
      plc1: formValue.plc1 ?? [],
      plc2: formValue.plc2 ?? [],
      tipiOlio: formValue.tipiOlio ?? [],
      segmentiMercato: formValue.segmentiMercato ?? [],
      badge: [...new Set(this.getActiveTechnicalFilters().map(filter => filter.value))],
    };

    if (formValue.disponibili) {
      searchObject['disponibili'] = true;
    }
    if (formValue.prezzoMin !== null) {
      searchObject['PrezzoMin'] = formValue.prezzoMin;
    }
    if (formValue.prezzoMax !== null) {
      searchObject['PrezzoMax'] = formValue.prezzoMax;
    }
    if (formValue.competitorMarchio) {
      searchObject['competitorMarchio'] = formValue.competitorMarchio;
    }
    if (formValue.competitorNomeProdotto) {
      searchObject['competitorNomeProdotto'] = formValue.competitorNomeProdotto;
    }

    const payload: any = {
      searchText: {
        text: formValue.searchText?.trim() ?? '',
      },
      searchObject,
    };

    if (withOrderBy) {
      payload['OrderBy'] = this.currentSortColumns.map(sort => ({
        PropertyName: this.sortPropertyMap[sort.column] ?? sort.column,
        OrderType: sort.direction === 'desc' ? 'Desc' : 'Asc',
      }));
    }

    return payload;
  }

  exportPriceList(format: 'xlsx' | 'pdf') {
    this.productsService.exportPriceList(format, this.buildSearchPayload(true))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `listino-agenti.${format}`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: error => {
          console.error(`Errore durante l'export del listino`, error);
        },
      });
  }

  private loadProducts() {
    const groupedView = this.selectedView === 'grouped';
    const payload = this.buildSearchPayload(!groupedView);

    const request$ = groupedView
      ? this.productsService.getGroupedProducts(this.pageIndex + 1, this.pageSize, payload)
      : this.productsService.getCatalogProducts(this.pageIndex + 1, this.pageSize, payload);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          if (groupedView) {
            this.groupedProducts = data.items;
          } else {
            this.catalogProducts = data.items;
            this.tableProducts = this.catalogProducts.map(p => ({
              ...p,
              'imballoDescrizione': `${p.nomeImballo} - ${p.unitaFatturabile} - ${p.unitaMisura}`,
            }));
          }
          this.totalItems = data.totalItems;
        },
        error: error => {
          console.error('Errore durante il caricamento dei prodotti', error);
        },
      });
  }

  private updateScrollState() {
    const offset = this._scrollable.measureScrollOffset('left');
    const el = this._scrollable.getElementRef().nativeElement;
    this.canScrollLeft.set(offset > 0);
    this.canScrollRight.set(offset + el.clientWidth < el.scrollWidth - 1);
  }

  // Scroll continuo mentre il tasto è tenuto premuto
  startScroll(direction: 'left' | 'right') {
    const step = direction === 'left' ? -8 : 8;
    this.scrollInterval = setInterval(() => {
      const current = this._scrollable.measureScrollOffset('left');
      this._scrollable.scrollTo({ left: current + step });
    }, 16);
  }

  stopScroll() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }

  scrollLeft() {
    const current = this._scrollable.measureScrollOffset('left');
    this._scrollable.scrollTo({ left: current - 300, behavior: 'smooth' });
  }

  scrollRight() {
    const current = this._scrollable.measureScrollOffset('left');
    this._scrollable.scrollTo({ left: current + 300, behavior: 'smooth' });
  }
}
